/**
 * Wave 4 — Estimate / Proposal / Submission readiness (domain fields only).
 * Prefer READY / BLOCKED / IN_PROGRESS — never invent completion %.
 */

export type StageReadinessState = 'READY' | 'BLOCKED' | 'IN_PROGRESS';

export type StageReadinessCheck = {
  id: string;
  group: string;
  label: string;
  state: StageReadinessState;
  detail?: string;
};

export type StageReadinessGroup = {
  name: string;
  state: StageReadinessState;
  checks: StageReadinessCheck[];
};

export type StageReadinessResult = {
  overall: StageReadinessState;
  checks: StageReadinessCheck[];
  groups: StageReadinessGroup[];
};

export type SubmissionBlocker = {
  id: string;
  reason: string;
  severity: 'blocking' | 'warning';
};

/** Canonical technical proposal section keys (checklist — not a %). */
export const DEFAULT_TECHNICAL_SECTIONS = [
  'Scope',
  'Methodology',
  'Schedule',
  'Organization',
  'HSE',
  'Quality',
] as const;

/** Pack checklist keys for tender submission. */
export const DEFAULT_SUBMISSION_PACK_KEYS = [
  'technical_proposal',
  'commercial_proposal',
  'bid_forms',
  'compliance',
] as const;

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function rollup(states: StageReadinessState[]): StageReadinessState {
  if (states.some((s) => s === 'BLOCKED')) return 'BLOCKED';
  if (states.length && states.every((s) => s === 'READY')) return 'READY';
  return 'IN_PROGRESS';
}

function groupify(checks: StageReadinessCheck[]): StageReadinessGroup[] {
  const names = Array.from(new Set(checks.map((c) => c.group)));
  return names.map((name) => {
    const gChecks = checks.filter((c) => c.group === name);
    return { name, state: rollup(gChecks.map((c) => c.state)), checks: gChecks };
  });
}

function parseJsonField(raw: unknown): unknown {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

/** Normalize sections JSON into { key, complete }[]. */
export function normalizeSections(raw: unknown): Array<{ key: string; complete: boolean }> {
  const parsed = parseJsonField(raw);
  if (!parsed) return [];
  if (Array.isArray(parsed)) {
    return parsed.map((item, idx) => {
      if (typeof item === 'string') {
        return { key: item, complete: true };
      }
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const key = asStr(o.key || o.title || o.name || `section-${idx}`);
        const status = asStr(o.status).toLowerCase();
        const complete =
          o.complete === true ||
          status === 'complete' ||
          status === 'ready' ||
          status === 'done';
        return { key, complete };
      }
      return { key: `section-${idx}`, complete: false };
    });
  }
  if (typeof parsed === 'object') {
    return Object.entries(parsed as Record<string, unknown>).map(([key, val]) => {
      if (typeof val === 'boolean') return { key, complete: val };
      const status = asStr(val).toLowerCase();
      return {
        key,
        complete: status === 'complete' || status === 'ready' || status === 'true' || status === 'done',
      };
    });
  }
  return [];
}

export function seedTechnicalSections(): Array<{ key: string; status: string }> {
  return DEFAULT_TECHNICAL_SECTIONS.map((key) => ({ key, status: 'missing' }));
}

export function seedCommercialSections(costGroups: readonly string[]): Array<{
  key: string;
  status: string;
}> {
  return costGroups.slice(0, 8).map((key) => ({ key, status: 'missing' }));
}

export function seedSubmissionPack(): Record<string, boolean> {
  const pack: Record<string, boolean> = {};
  for (const k of DEFAULT_SUBMISSION_PACK_KEYS) pack[k] = false;
  return pack;
}

export function buildEstimateSummaryReadiness(input: {
  estimateCount: number;
  approvedRevisionCount?: number;
  latestRevisionStatus?: string | null;
}): StageReadinessResult {
  const checks: StageReadinessCheck[] = [];
  const approved =
    input.approvedRevisionCount ??
    (/^approved$/i.test(String(input.latestRevisionStatus || '')) ? 1 : 0);

  if (input.estimateCount === 0) {
    checks.push({
      id: 'est-estimate',
      group: 'Estimate',
      label: 'EST3 estimate',
      state: 'IN_PROGRESS',
      detail: 'No EPC estimate yet — open Estimating Workbench',
    });
  } else if (approved > 0) {
    checks.push({
      id: 'est-estimate',
      group: 'Estimate',
      label: 'Approved EST3 revision',
      state: 'READY',
      detail: `${approved} approved revision(s) · ${input.estimateCount} estimate(s)`,
    });
  } else {
    checks.push({
      id: 'est-estimate',
      group: 'Estimate',
      label: 'EST3 estimate present',
      state: 'IN_PROGRESS',
      detail: input.latestRevisionStatus
        ? `Revision status: ${input.latestRevisionStatus}`
        : `${input.estimateCount} estimate(s) — none approved yet`,
    });
  }

  return { overall: rollup(checks.map((c) => c.state)), checks, groups: groupify(checks) };
}

export function buildProposalReadiness(row: Record<string, unknown>): StageReadinessResult {
  const checks: StageReadinessCheck[] = [];
  const title = asStr(row.title);
  const tenderId = asStr(row.tender_id || row.tenderId);
  const currency = asStr(row.currency);
  const value = row.commercial_value ?? row.commercialValue;
  const hasValue = value != null && value !== '' && !Number.isNaN(Number(value));
  const tech = normalizeSections(row.technical_sections_json ?? row.technicalSectionsJson);
  const comm = normalizeSections(row.commercial_sections_json ?? row.commercialSectionsJson);
  const status = asStr(row.status || 'Draft').toLowerCase();

  checks.push({
    id: 'prop-tender',
    group: 'Linkage',
    label: 'Tender linked',
    state: tenderId ? 'READY' : 'BLOCKED',
  });
  checks.push({
    id: 'prop-title',
    group: 'Identity',
    label: 'Title',
    state: title ? 'READY' : 'IN_PROGRESS',
  });
  checks.push({
    id: 'prop-value',
    group: 'Commercial',
    label: 'Commercial value',
    state: hasValue ? 'READY' : 'BLOCKED',
    detail: hasValue ? undefined : 'Required for award reconciliation later',
  });
  checks.push({
    id: 'prop-currency',
    group: 'Commercial',
    label: 'Currency',
    state: currency ? 'READY' : 'IN_PROGRESS',
  });

  const techComplete = tech.length ? tech.filter((s) => s.complete).length : 0;
  const techTotal = tech.length || DEFAULT_TECHNICAL_SECTIONS.length;
  checks.push({
    id: 'prop-tech',
    group: 'Technical sections',
    label: 'Technical sections',
    state:
      tech.length === 0
        ? 'IN_PROGRESS'
        : tech.every((s) => s.complete)
          ? 'READY'
          : techComplete > 0
            ? 'IN_PROGRESS'
            : 'BLOCKED',
    detail: tech.length
      ? `${techComplete}/${tech.length} complete`
      : `No sections seeded (${techTotal} expected)`,
  });

  const commComplete = comm.filter((s) => s.complete).length;
  checks.push({
    id: 'prop-comm',
    group: 'Commercial sections',
    label: 'Commercial sections',
    state:
      comm.length === 0
        ? 'IN_PROGRESS'
        : comm.every((s) => s.complete)
          ? 'READY'
          : commComplete > 0
            ? 'IN_PROGRESS'
            : 'BLOCKED',
    detail: comm.length ? `${commComplete}/${comm.length} complete` : 'No commercial sections',
  });

  if (['approved', 'submitted', 'issued'].includes(status)) {
    checks.push({
      id: 'prop-status',
      group: 'Status',
      label: `Status: ${asStr(row.status)}`,
      state: 'READY',
    });
  } else {
    checks.push({
      id: 'prop-status',
      group: 'Status',
      label: `Status: ${asStr(row.status) || 'Draft'}`,
      state: 'IN_PROGRESS',
    });
  }

  return { overall: rollup(checks.map((c) => c.state)), checks, groups: groupify(checks) };
}

export function buildSubmissionReadiness(input: {
  submission: Record<string, unknown>;
  proposal?: Record<string, unknown> | null;
  hasApprovedGo?: boolean;
  submissionDueAt?: string | null;
  openClarificationCount?: number;
  unincorporatedAddendaCount?: number;
  bidBondReady?: boolean;
  today?: Date;
  /** EST3 Wave 7 — from GET readiness or proposal submission-basis API */
  est3SubmissionBasisStatus?: string | null;
  est3ReadinessBlockers?: Array<{ message: string }>;
}): StageReadinessResult & {
  blockers: SubmissionBlocker[];
  canSubmit: boolean;
  submitBlockedReason?: string;
} {
  const { submission, proposal, hasApprovedGo } = input;
  const today = input.today ?? new Date();
  const checks: StageReadinessCheck[] = [];
  const blockers: SubmissionBlocker[] = [];

  const tenderId = asStr(submission.tender_id || submission.tenderId);
  const proposalId = asStr(submission.proposal_id || submission.proposalId);
  const status = asStr(submission.status || 'Draft');
  const pack = parseJsonField(submission.pack_json ?? submission.packJson) as Record<
    string,
    unknown
  > | null;

  if (!tenderId) {
    blockers.push({ id: 'tender', reason: 'Submission must link a tender', severity: 'blocking' });
  }
  checks.push({
    id: 'sub-tender',
    group: 'Linkage',
    label: 'Tender',
    state: tenderId ? 'READY' : 'BLOCKED',
  });

  if (!proposalId) {
    blockers.push({
      id: 'proposal',
      reason: 'Link a proposal before submit',
      severity: 'blocking',
    });
  }
  checks.push({
    id: 'sub-proposal',
    group: 'Linkage',
    label: 'Proposal linked',
    state: proposalId ? 'READY' : 'BLOCKED',
  });

  if (proposal) {
    const propReady = buildProposalReadiness(proposal);
    if (propReady.overall === 'BLOCKED') {
      blockers.push({
        id: 'proposal-ready',
        reason: 'Linked proposal has BLOCKED readiness (value / sections)',
        severity: 'blocking',
      });
    } else if (propReady.overall === 'IN_PROGRESS') {
      blockers.push({
        id: 'proposal-progress',
        reason: 'Linked proposal still IN_PROGRESS',
        severity: 'warning',
      });
    }
    checks.push({
      id: 'sub-proposal-ready',
      group: 'Proposal',
      label: 'Proposal readiness',
      state: propReady.overall,
    });

    const meta = parseJsonField(proposal.meta_json ?? proposal.metaJson) as Record<
      string,
      unknown
    > | null;
    const pin = (meta?.est3_pricing_pin || meta?.est3PricingPin) as Record<string, unknown> | null;
    const basisStatus =
      asStr(input.est3SubmissionBasisStatus) ||
      (pin?.estimate_revision_id ? 'CERTIFIED' : 'NOT_RECORDED');
    if (basisStatus === 'STALE') {
      blockers.push({
        id: 'est3-basis-stale',
        reason: 'EST3 submission basis is STALE — re-certify after addenda review',
        severity: 'blocking',
      });
      checks.push({
        id: 'sub-est3-basis',
        group: 'EST3',
        label: 'Submission basis',
        state: 'BLOCKED',
        detail: 'STALE',
      });
    } else if (basisStatus === 'CERTIFIED') {
      checks.push({
        id: 'sub-est3-basis',
        group: 'EST3',
        label: 'Submission basis',
        state: 'READY',
        detail: 'CERTIFIED',
      });
    } else if (basisStatus === 'NOT_RECORDED' || basisStatus === 'NOT_CERTIFIED') {
      blockers.push({
        id: 'est3-basis-missing',
        reason:
          basisStatus === 'NOT_RECORDED'
            ? 'Estimate basis not recorded on proposal'
            : 'EST3 submission basis not certified',
        severity: 'blocking',
      });
      checks.push({
        id: 'sub-est3-basis',
        group: 'EST3',
        label: 'Submission basis',
        state: 'BLOCKED',
        detail: basisStatus,
      });
    }
  }

  for (const b of input.est3ReadinessBlockers || []) {
    blockers.push({
      id: `est3-${blockers.length}`,
      reason: b.message,
      severity: 'blocking',
    });
  }

  if (hasApprovedGo === false) {
    blockers.push({
      id: 'bid-go',
      reason: 'No approved GO / CONDITIONAL bid decision for this tender',
      severity: 'blocking',
    });
    checks.push({
      id: 'sub-bid',
      group: 'Governance',
      label: 'Approved bid GO',
      state: 'BLOCKED',
    });
  } else if (hasApprovedGo === true) {
    checks.push({
      id: 'sub-bid',
      group: 'Governance',
      label: 'Approved bid GO',
      state: 'READY',
    });
  }

  const dueRaw = asStr(input.submissionDueAt || '');
  if (dueRaw) {
    const due = new Date(dueRaw.length <= 10 ? `${dueRaw}T00:00:00` : dueRaw);
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (!Number.isNaN(due.getTime()) && due < start && !['Approved', 'Submitted'].includes(status)) {
      blockers.push({
        id: 'deadline',
        reason: 'Submission due date has passed',
        severity: 'blocking',
      });
      checks.push({
        id: 'sub-deadline',
        group: 'Deadline',
        label: 'Submission due',
        state: 'BLOCKED',
        detail: dueRaw.slice(0, 10),
      });
    } else {
      checks.push({
        id: 'sub-deadline',
        group: 'Deadline',
        label: 'Submission due',
        state: 'READY',
        detail: dueRaw.slice(0, 10),
      });
    }
  } else {
    checks.push({
      id: 'sub-deadline',
      group: 'Deadline',
      label: 'Submission due',
      state: 'IN_PROGRESS',
      detail: 'Not set on tender',
    });
  }

  const packObj =
    pack && typeof pack === 'object' && !Array.isArray(pack)
      ? (pack as Record<string, unknown>)
      : null;
  const checklist =
    packObj && packObj.checklist && typeof packObj.checklist === 'object'
      ? (packObj.checklist as Record<string, unknown>)
      : packObj;

  let packReadyCount = 0;
  let packTotal: number = DEFAULT_SUBMISSION_PACK_KEYS.length;
  if (checklist) {
    const entries = Object.entries(checklist).filter(([k]) => k !== 'notes');
    packTotal = Math.max(entries.length, 1);
    packReadyCount = entries.filter(([, v]) => v === true).length;
    if (packReadyCount < entries.length) {
      blockers.push({
        id: 'pack',
        reason: `Submission pack incomplete (${packReadyCount}/${entries.length} items)`,
        severity: 'blocking',
      });
    }
  } else {
    blockers.push({
      id: 'pack',
      reason: 'Submission pack checklist not started',
      severity: 'blocking',
    });
  }

  checks.push({
    id: 'sub-pack',
    group: 'Pack',
    label: 'Submission pack',
    state:
      !checklist
        ? 'BLOCKED'
        : packReadyCount >= packTotal
          ? 'READY'
          : packReadyCount > 0
            ? 'IN_PROGRESS'
            : 'BLOCKED',
    detail: checklist ? `${packReadyCount}/${packTotal} checked` : undefined,
  });

  const openClar = input.openClarificationCount ?? 0;
  if (openClar > 0) {
    blockers.push({
      id: 'clarifications',
      reason: `${openClar} clarification(s) still open`,
      severity: 'blocking',
    });
    checks.push({
      id: 'sub-clar',
      group: 'Clarifications',
      label: 'Clarifications closed',
      state: 'BLOCKED',
      detail: `${openClar} open`,
    });
  } else {
    checks.push({
      id: 'sub-clar',
      group: 'Clarifications',
      label: 'Clarifications closed',
      state: 'READY',
    });
  }

  const pendingAddenda = input.unincorporatedAddendaCount ?? 0;
  if (pendingAddenda > 0) {
    blockers.push({
      id: 'addenda',
      reason: `${pendingAddenda} addendum(addenda) not incorporated`,
      severity: 'blocking',
    });
    checks.push({
      id: 'sub-addenda',
      group: 'Addenda',
      label: 'Addenda incorporated',
      state: 'BLOCKED',
      detail: `${pendingAddenda} pending`,
    });
  } else {
    checks.push({
      id: 'sub-addenda',
      group: 'Addenda',
      label: 'Addenda incorporated',
      state: 'READY',
    });
  }

  if (input.bidBondReady === false) {
    blockers.push({
      id: 'bid-bond',
      reason: 'Bid bond not ready',
      severity: 'blocking',
    });
    checks.push({
      id: 'sub-bond',
      group: 'Bid bond',
      label: 'Bid bond',
      state: 'BLOCKED',
    });
  } else if (input.bidBondReady === true) {
    checks.push({
      id: 'sub-bond',
      group: 'Bid bond',
      label: 'Bid bond',
      state: 'READY',
    });
  }

  if (['Submitted', 'Approved'].includes(status)) {
    checks.push({
      id: 'sub-status',
      group: 'Approval',
      label: `Status: ${status}`,
      state: status === 'Approved' ? 'READY' : 'IN_PROGRESS',
    });
  } else {
    checks.push({
      id: 'sub-status',
      group: 'Approval',
      label: `Status: ${status}`,
      state: 'IN_PROGRESS',
      detail: 'Submit starts Approval Runtime',
    });
  }

  const hardBlockers = blockers.filter((b) => b.severity === 'blocking');
  let canSubmit = false;
  let submitBlockedReason: string | undefined;
  if (!['Draft', 'Rejected'].includes(status)) {
    submitBlockedReason = `Status ${status} cannot be submitted`;
  } else if (hardBlockers.length) {
    submitBlockedReason = hardBlockers[0].reason;
  } else {
    canSubmit = true;
  }

  return {
    overall: rollup(checks.map((c) => c.state)),
    checks,
    groups: groupify(checks),
    blockers,
    canSubmit,
    submitBlockedReason,
  };
}

export function readinessTagColor(state: StageReadinessState): string {
  switch (state) {
    case 'READY':
      return 'success';
    case 'BLOCKED':
      return 'error';
    default:
      return 'processing';
  }
}
